import { Box, Typography, Stack, Tooltip } from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AnimatedNumber from "./AnimatedNumber";

// Map a rank to a medal/trophy icon and tooltip text. Returns null if the
// rank doesn't qualify for highlighting (i.e. not in top 3 and not top 10%).
function rankBadge(rank, total) {
  if (!rank || !total) return null;
  if (rank === 1) return { emoji: "🏆", color: "#fbbf24", label: `All-time best of ${total}` };
  if (rank === 2) return { emoji: "🥈", color: "#9ca3af", label: `#2 of ${total} ever` };
  if (rank === 3) return { emoji: "🥉", color: "#d97706", label: `#3 of ${total} ever` };
  const pct = (rank / total) * 100;
  if (pct <= 10) return { emoji: "⭐", color: "#818cf8", label: `Top 10% (#${rank} of ${total})` };
  if (pct <= 25) return { emoji: "✨", color: "#a5b4fc", label: `Top 25% (#${rank} of ${total})` };
  return null;
}

export default function StatCard({
  label,
  value,           // pre-formatted string (legacy / fallback)
  numericValue,    // raw number; if provided alongside `formatter`, animates
  formatter,       // (n) => string
  sublabel,
  valueColor,
  icon,
  personalBest,    // { rank, total, delta_pct } — both fields optional
}) {
  const animate = numericValue !== undefined && typeof formatter === "function";
  const badge = personalBest ? rankBadge(personalBest.rank, personalBest.total) : null;
  const delta = personalBest?.delta_pct;
  const hasDelta = delta !== null && delta !== undefined && Number.isFinite(delta);
  const deltaPositive = hasDelta && delta >= 0;

  return (
    <Box
      sx={{
        position: "relative",
        p: 2.25,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        minWidth: 0,
        overflow: "hidden",
        transition: "transform 180ms cubic-bezier(.4,0,.2,1), box-shadow 180ms, border-color 180ms",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
          borderColor: "primary.light",
        },
      }}
    >
      {badge && (
        <Tooltip title={badge.label} arrow placement="top">
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              px: 0.75,
              py: 0.25,
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: badge.color,
              bgcolor: `${badge.color}1a`,
              lineHeight: 1,
            }}
          >
            <span style={{ fontSize: 12 }}>{badge.emoji}</span>
            {personalBest.rank <= 3 ? `#${personalBest.rank}` : `#${personalBest.rank}/${personalBest.total}`}
          </Box>
        </Tooltip>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5, pr: badge ? 6 : 0 }}>
        <Typography sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "text.secondary", fontWeight: 600 }}>
          {label}
        </Typography>
        {icon && !badge && (
          <Box sx={{ color: "text.disabled", display: "flex" }}>{icon}</Box>
        )}
      </Stack>
      <Typography
        sx={{
          fontFamily: '"Geist Sans", sans-serif',
          fontWeight: 600,
          fontSize: 26,
          letterSpacing: "-0.02em",
          color: valueColor || "text.primary",
          lineHeight: 1.1,
          mt: 0.5,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {animate ? <AnimatedNumber value={numericValue} formatter={formatter} /> : value}
      </Typography>
      {(sublabel || hasDelta) && (
        <Stack direction="row" alignItems="center" gap={0.75} sx={{ mt: 0.5, flexWrap: "wrap" }}>
          {sublabel && (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {sublabel}
            </Typography>
          )}
          {hasDelta && (
            <Tooltip title="vs prior period of same length" arrow placement="top">
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: deltaPositive ? "success.main" : "error.main",
                  bgcolor: deltaPositive ? "rgba(52,211,153,0.12)" : "rgba(248,113,113,0.12)",
                  borderRadius: 999,
                  px: 0.6,
                  py: 0.1,
                  lineHeight: 1.2,
                }}
              >
                {deltaPositive ? <ArrowDropUpIcon sx={{ fontSize: 14 }} /> : <ArrowDropDownIcon sx={{ fontSize: 14 }} />}
                {Math.abs(delta).toFixed(1)}%
              </Box>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
}

